import { BaseService } from './base.service';
import type {
  CurrentWeekResponse,
  GameJourney,
  GamePlanActivity,
  GoalBoard,
  LogActivityDto,
  OnboardGamePlanDto,
  OverviewResponse,
  PlanImpact,
  SetTaskCompletionDto,
  UpdatePlanSettingsDto,
} from '@/types/gamePlanTracker';

// Owner-facing Game Plan tracker API (api/src/modules/game-plan). Distinct from
// the read-only content-fragments playbook service (game-plan.service.ts).
class GamePlanTrackerService extends BaseService {
  constructor() {
    super('/game-plan');
  }

  getOverview(locationId: string): Promise<OverviewResponse> {
    return this.get<OverviewResponse>(`/${locationId}`);
  }

  getCurrentWeek(locationId: string, week?: number): Promise<CurrentWeekResponse> {
    const q = week ? `?week=${week}` : '';
    return this.get<CurrentWeekResponse>(`/${locationId}/current-week${q}`);
  }

  getGoalBoard(locationId: string): Promise<GoalBoard> {
    return this.get<GoalBoard>(`/${locationId}/goal-board`);
  }

  getJourney(locationId: string): Promise<GameJourney> {
    return this.get<GameJourney>(`/${locationId}/journey`);
  }

  onboard(
    locationId: string,
    dto: OnboardGamePlanDto,
  ): Promise<CurrentWeekResponse> {
    return this.post<CurrentWeekResponse>(`/${locationId}/onboard`, dto);
  }

  // Dry run for an edit — what the module would look like with these inputs.
  getPlanImpact(
    locationId: string,
    goal?: number,
    grandOpeningDate?: string,
  ): Promise<PlanImpact> {
    const params = new URLSearchParams();
    if (goal != null) params.set('goal', String(goal));
    if (grandOpeningDate) params.set('grandOpeningDate', grandOpeningDate);
    const q = params.toString();
    return this.get<PlanImpact>(`/${locationId}/impact${q ? `?${q}` : ''}`);
  }

  updatePlanSettings(
    locationId: string,
    dto: UpdatePlanSettingsDto,
  ): Promise<CurrentWeekResponse> {
    return this.put<CurrentWeekResponse>(`/${locationId}/settings`, dto);
  }

  logActivity(
    locationId: string,
    dto: LogActivityDto,
  ): Promise<GamePlanActivity> {
    return this.post<GamePlanActivity>(`/${locationId}/activities`, dto);
  }

  updateActivity(
    locationId: string,
    activityId: string,
    dto: LogActivityDto,
  ): Promise<GamePlanActivity> {
    return this.put<GamePlanActivity>(
      `/${locationId}/activities/${activityId}`,
      dto,
    );
  }

  deleteActivity(locationId: string, activityId: string): Promise<void> {
    return this.delete<void>(`/${locationId}/activities/${activityId}`);
  }

  setTaskCompletion(
    locationId: string,
    dto: SetTaskCompletionDto,
  ): Promise<{ taskId: string; week: number; completed: boolean }> {
    return this.post<{ taskId: string; week: number; completed: boolean }>(
      `/${locationId}/tasks`,
      dto,
    );
  }
}

export const gamePlanTrackerService = new GamePlanTrackerService();
